import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTable, useSortBy, useFilters } from 'react-table';

import { FaEye, FaFileContract } from 'react-icons/fa';

import { toast } from 'react-toastify';
import SearchFilter from '~/components/Table/Filters/SearchFilter';
import SelectColumnFilterForStatus from './components/SelectFilterForStatus';
import SelectColumnFilterForPayment from './components/SelectFilterForPayment';
import { PaymentBadge, StatusBadge, ActionsContainer, Action } from './styles';
import api from '~/services/api';
import history from '~/services/history';

export default function Table({ data }) {
  const columns = useMemo(() => {
    const defaultColumns = [
      {
        Header: 'ID',
        accessor: 'id',
        disableFilters: true,
        disableSortBy: true,
      },
      {
        Header: 'Client Name',
        accessor: item => {
          return item.client.fullName;
        },
        disableSortBy: true,
      },
      {
        Header: 'Vehicle',
        accessor: item => {
          return item.vehicle
            ? `${item.vehicle.displayName} (${item.vehicle.vin})`
            : 'REMOVED';
        },
        disableSortBy: true,
      },
      {
        Header: 'Term',
        accessor: 'term',
        disableSortBy: true,
        disableFilters: true,
      },
      {
        Header: 'Start Date',
        accessor: 'start',
        disableSortBy: true,
        disableFilters: true,
      },
      {
        Header: 'End Date',
        accessor: 'end',
        disableSortBy: true,
        disableFilters: true,
      },
      {
        Header: 'Status',
        accessor: item => {
          let status;
          if (item.status === null) {
            status = 'pending';
          } else if (item.status === true) {
            status = 'active';
          } else if (item.status === false) {
            status = 'finished';
          }
          return <StatusBadge value={status}>{status}</StatusBadge>;
        },
        Filter: SelectColumnFilterForStatus,
        filter: 'status',
        disableSortBy: true,
      },
      {
        Header: 'Payment',
        accessor: item => {
          let status;
          let appendString;
          let shouldBeYellow = false;
          if (item.pastDueCharges !== null) {
            if (item.status === false) {
              status = 'finished';
            } else if (item.pastDueCharges.length === 0) {
              status = 'up to date';
            } else if (item.pastDueCharges.length >= 1) {
              status = 'past due';
              if (
                item.pastDueCharges[0].notes &&
                item.pastDueCharges[0].notes
                  .toLowerCase()
                  .includes('reschedule')
              ) {
                shouldBeYellow = true;
                appendString = `${item.pastDueCharges[0].notes
                  .toLowerCase()
                  .replace('reschedule:', '')}`;
              } else {
                appendString = ` ${item.pastDueCharges[0].due_date}`;
              }
            }
          } else {
            status = 'unknown';
          }
          return (
            <PaymentBadge shouldBeYellow={shouldBeYellow} value={status}>
              {status}
              {appendString}
            </PaymentBadge>
          );
        },
        Filter: SelectColumnFilterForPayment,
        filter: 'payment',
        disableSortBy: true,
      },
      {
        Header: 'Balance',
        disableFilters: true,
        accessor: item => {
          const charges =
            item.sumCharges &&
            item.sumCharges[0] &&
            item.sumCharges[0].totalcharges
              ? Number(item.sumCharges[0].totalcharges)
              : 0;
          const payments =
            item.sumPayments &&
            item.sumPayments[0] &&
            item.sumPayments[0].totalpayments
              ? Number(item.sumPayments[0].totalpayments)
              : 0;
          return (payments - charges).toFixed(2);
        },
      },
      {
        Header: 'Actions',
        accessor: item => (
          <ActionsContainer>
            <Action
              onClick={() =>
                history.push({
                  pathname: `/subscription/${item.id}`,
                })
              }
            >
              <FaEye />
              <span>View</span>
            </Action>
            <Action
              onClick={async () => {
                try {
                  await api.get(`subscription/${item.id}/download`);
                  toast.success(
                    'The contract will be sent to your email in the next seconds...'
                  );
                } catch (err) {
                  toast.error(
                    err.response &&
                      err.response.data &&
                      err.response.data.error &&
                      typeof err.response.data.error === 'string'
                      ? err.response.data.error
                      : 'Error while generating pdf'
                  );
                }
              }}
            >
              <FaFileContract />
              <span>Contract</span>
            </Action>
          </ActionsContainer>
        ),
        disableFilters: true,
        disableSortBy: true,
      },
    ];
    // const adminColumn = isAdmin && {
    //   id: 'admin',
    //   Header: () => <FaExclamationTriangle color="#CA0B00" />,
    //   accessor: item => {
    //     return (
    //       <ActionsContainer>
    //         <Action onClick={() => handleDelete(item.id)}>
    //           <MdDeleteForever color="#CA0B00" size={22} />
    //           <span>Delete</span>
    //         </Action>
    //       </ActionsContainer>
    //     );
    //   },
    //   disableFilters: true,
    //   disableSortBy: true,
    // };
    // if (isAdmin) {
    //   return [...defaultColumns, adminColumn];
    // }
    return defaultColumns;
  }, []);
  const defaultColumn = React.useMemo(() => ({ Filter: SearchFilter }), []);
  const filterTypes = React.useMemo(
    () => ({
      status: (rows, id, filterValue) => {
        return rows.filter(row => {
          const rowValue = row.values[id];
          return rowValue !== undefined &&
            row.values[id].props !== undefined &&
            row.values[id].props.children !== undefined
            ? String(row.values[id].props.children)
                .toLowerCase()
                .startsWith(String(filterValue).toLowerCase())
            : true;
        });
      },
      payment: (rows, id, filterValue) => {
        return rows.filter(row => {
          const rowValue = row.values[id];
          return rowValue !== undefined &&
            row.values[id].props !== undefined &&
            row.values[id].props.children !== undefined
            ? String(row.values[id].props.children)
                .toLowerCase()
                .startsWith(String(filterValue).toLowerCase())
            : true;
        });
      },
    }),
    []
  );
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable(
    {
      columns,
      defaultColumn,
      filterTypes,
      data,
    },
    useFilters,
    useSortBy
  );
  return (
    <>
      <table {...getTableProps()}>
        <thead>
          {headerGroups.map(headerGroup => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map(column => (
                <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                  {column.render('Header')}
                  {column.isSorted ? (
                    <span>{column.isSortedDesc ? ' ↓' : ' ↑'}</span>
                  ) : (
                    ' '
                  )}
                  {column.canFilter && <div> {column.render('Filter')} </div>}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map(row => {
            prepareRow(row);
            return (
              <tr {...row.getRowProps()}>
                {row.cells.map(cell => {
                  return (
                    <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </>
  );
}

Table.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape()).isRequired,
};
